import { NgClass } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { L10n } from '@skylab-angular-shared/core';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { IValueConverter, L10nBase } from '@skylab-shared/core';
import { dateDiffInDays, getMonthRange } from '@skylab-shared/utils';
import { ButtonComponent } from '../../../../button/button.component';
import { InsDatepickerDirective } from '../../../../datepicker/datepicker.directive';
import { DialogContentComponentBase } from '../../../../dialog/dialog-content-base';
import { DialogContentComponentIns } from '../../../../dialog/dialog-content-ins.component';
import { DialogHeaderComponentIns } from '../../../../dialog/dialog-header-ins.component';
import { DialogInsService } from '../../../../dialog/dialog-ins.service';
import { DialogState } from '../../../../dialog/dialog.model';
import { FocusByDirective } from '../../../../focus-by/focus-by.directive';
import { FormContainerComponent } from '../../../../form-container/form-container.component';
import { TooltipDirective } from '../../../../tooltip/tooltip.directive';
import { DatepickerValueAccessorDirective } from '../../../../value-accessors/datepicker-value-accessor.directive';
import { SelectValueAccessorDirective } from '../../../../value-accessors/select-value-accessor.directive';
import { IconComponent } from '../../../../icon/icon.component';

export enum PeriodKind {
    FromTo = 'FromTo',
    Day = 'Day',
    Month = 'Month'
}

export interface FilterDateData {
    title: string;
    values: {
        from: Date;
        to: Date;
    };
}

export interface FilterDateResults {
    from: Date;
    to: Date;
}

interface FilterDateViewModel extends FilterDateResults {
    title: string;
    period: PeriodKind;
    periods: { label: string; value: PeriodKind }[];
    periodConverter: IValueConverter<string, { label: string; value: string; }>;
    chosenDay: Date;
    chosenMonth: Date;
    isDateShown(kind: string): boolean;
    isFromToPeriodInvalid(): boolean;
}

@Component({
    selector: 'skl-filter-date-dialog',
    templateUrl: './filter-date-dialog.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, FormContainerComponent, NgClass, FocusByDirective, SelectValueAccessorDirective, FormsModule, InsDatepickerDirective, DatepickerValueAccessorDirective, TooltipDirective, ButtonComponent, TranslatePipe, IconComponent]
})
export class FilterDateDialogComponent extends DialogContentComponentBase<FilterDateViewModel, FilterDateData>  {
    private initFilterValues!: { from: Date, to: Date } | null;

    public constructor(
        @Inject(L10n) private readonly l10n: L10nBase,
        dialogService: DialogInsService
    ) {
        super(dialogService);
    }

    protected override getInitialState(): FilterDateViewModel {
        this.initFilterValues = this.data?.values?.from && this.data?.values?.to ? {...this.data.values} : null;

        return {
            ...super.getInitialState(),
            title: this.data.title,
            period: this.initFilterValues ? this.recognizeDate(this.initFilterValues) : PeriodKind.FromTo,
            periods: this.getPeriods(),
            periodConverter: {
                convert: (value: string) => {
                    return this.getPeriods().find(item => item.value === value) ?? { value: PeriodKind.FromTo, label: '' };
                },
                convertBack: (item: { label: string; value: string }) => {
                    return item.value;
                },
            },
            isDateShown: (kind: string) => {
                return this.viewModel.period === kind;
            },
            isFromToPeriodInvalid: () => {
                if (this.viewModel.period !== PeriodKind.FromTo) return false;

                const daysDiff = dateDiffInDays(this.viewModel.to, this.viewModel.from);
                return daysDiff < 0;
            }
        };
    }

    protected override async prepareDataAsync(): Promise<void> {
        if (this.initFilterValues === null) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            this.viewModel.chosenDay = today;

            const previousMonth = new Date();
            previousMonth.setMonth(today.getMonth() - 1);
            const prevMonthRange = getMonthRange(previousMonth);
            this.viewModel.from = prevMonthRange.firstDay;
            this.viewModel.to = prevMonthRange.lastDay;
            this.viewModel.chosenMonth = prevMonthRange.firstDay;
        } else {
            this.viewModel.from = this.initFilterValues.from;
            this.viewModel.to = this.initFilterValues.to;
            this.viewModel.chosenDay = this.initFilterValues.from;
            this.viewModel.chosenMonth = getMonthRange(this.initFilterValues.from).firstDay;
        }
    }

    private getPeriods(): { value: PeriodKind; label: string }[] {
        return [{
            value: PeriodKind.Month,
            label: this.l10n._T('ui.filter-chips.filter-date-dialog.form.chosen-month')
        },
        {
            value: PeriodKind.Day,
            label: this.l10n._T('ui.filter-chips.filter-date-dialog.form.chosen-day')
        },
        {
            value: PeriodKind.FromTo,
            label: this.l10n._T('ui.filter-chips.filter-date-dialog.form.dates-range')
        }];
    }

    private recognizeDate(value: { from: Date, to: Date }): PeriodKind {
        if (value === null) return PeriodKind.FromTo;

        if (dateDiffInDays(value.from, value.to) === 0) { // same day
            return PeriodKind.Day;
        } else {
            const valueMonthRange = getMonthRange(value.from);
            if (dateDiffInDays(valueMonthRange.firstDay, value.from) === 0 &&
                dateDiffInDays(valueMonthRange.lastDay, value.to) === 0) { // first and last day of month
                return PeriodKind.Month;
            }
        }
        return PeriodKind.FromTo;
    }

    public chooseDate(): void {
        if (this.viewModel.isFromToPeriodInvalid()) return;
        this.dialogService.close({
            state: DialogState.Confirm,
            data: this.getResultData()
        });
    }

    public getResultData(): { from: Date, to: Date } {
        switch (this.viewModel.period) {
            case PeriodKind.FromTo:
                return { from: this.viewModel.from, to: this.viewModel.to };
            case PeriodKind.Day:
                return { from: this.viewModel.chosenDay, to: this.viewModel.chosenDay };
            case PeriodKind.Month:
                const monthRange = getMonthRange(this.viewModel.chosenMonth);
                return { from: monthRange.firstDay, to: monthRange.lastDay };
        }
    }
}
